/**
 * PurchaseFlowV3 - block-configured quiz with product scoring (/pages/quiz)
 *
 * Everything the visitor sees and every rule comes from the section's blocks
 * (sections/pleto-purchase-flow-v3.liquid), rendered into a JSON config:
 * - step blocks      content, input, single-choice, multi-choice, consent, email, end, result
 * - product blocks   one per recommendable product (key, product, generic message)
 * - message blocks   "if <field> has <value>, the message for <product_key> is …"
 *
 * Options are one per line: value||Label||points||notice||tip||tip product
 *   points:  daily:3, magnesium:2      add points
 *            !omega3                   exclude that product from the result
 *   tip:     a personal line on the result ("Según tus respuestas"); with a tip
 *            product, shown only when that product is in the result
 *
 * Pure functions (compile, buildSequence, recommend) have no DOM access so
 * scripts/test-purchase-flow-v3.js can run them in Node against the template.
 *
 * Klaviyo: the email, first name and recommended products; with the section's
 * "Send answers to Klaviyo" on, every answer as quiz_* properties (klaviyoProperties).
 * Meta receives a bare "Lead".
 * See docs/purchase-flow-v3.md.
 */
(function () {
  'use strict';

  // The theme editor re-runs scripts inside a re-rendered section; the first load's
  // shopify:section:load listener already re-inits, so a second copy must not.
  if (typeof window !== 'undefined' && window.PurchaseFlowV3Logic) return;

  const STEP_TYPES = ['content', 'input', 'single-choice', 'multi-choice', 'consent', 'email', 'end', 'result'];
  const TERMINAL_TYPES = ['end', 'result'];
  const MAX_TIPS = 3;


  // ==================== Compile config → model (pure) ====================

  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  const list = (v) => String(v || '').split(',').map(norm).filter(Boolean);

  /** "daily:3, magnesium:2, !omega3" → { points: {daily: 3, magnesium: 2}, exclude: ['omega3'] } */
  function parsePoints(text) {
    const points = {};
    const exclude = [];
    String(text || '').split(',').map((t) => t.trim()).filter(Boolean).forEach((token) => {
      if (token.startsWith('!')) {
        exclude.push(norm(token.slice(1)));
        return;
      }
      const [key, value] = token.split(':').map((t) => t.trim());
      const n = parseFloat(value);
      if (key && !isNaN(n)) points[norm(key)] = (points[norm(key)] || 0) + n;
    });
    return { points, exclude };
  }

  /** Textarea → [{ value, label, points, exclude, notice, tip, tipProduct }] */
  function parseOptions(text) {
    return String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [value, label, points, notice, tip, tipProduct] = line.split('||').map((p) => (p || '').trim());
        return Object.assign(
          { value: norm(value), label: label || value, notice: notice || '', tip: tip || '', tipProduct: norm(tipProduct) },
          parsePoints(points)
        );
      });
  }

  /** "goals=digestion; activity=alta" → [{ field: 'goals', values: ['digestion'] }, …]. */
  function parsePresets(raw) {
    return String(raw || '')
      .split(/[;\n]/)
      .map((part) => part.split('='))
      .filter((pair) => pair.length === 2 && norm(pair[0]))
      .map(([field, values]) => ({ field: norm(field), values: list(values) }))
      .filter((p) => p.values.length);
  }

  function parseCondition(field, operator, value) {
    if (!field || !operator || operator === 'always') return null;
    return { field: norm(field), operator, value: value || '' };
  }

  function compile(config) {
    // V2-style needs the agreement text under the email button; without it, stay explicit.
    const impliedText = (config.blocks || []).some((b) => b.type === 'email' && !b.settings?.hide_step && String(b.settings?.implied_text || '').trim());
    const consentMode = config.settings?.consentMode === 'implied' && impliedText ? 'implied' : 'explicit';
    if (config.settings?.consentMode === 'implied' && !impliedText && typeof console !== 'undefined') {
      console.warn('PurchaseFlowV3: V2-style consent needs "Text under the button" on the Email block; using the Consent screen');
    }
    const steps = [];
    const products = {};
    const messages = [];
    const entries = [];

    (config.blocks || []).forEach((block) => {
      const s = block.settings || {};
      if (block.type === 'product') {
        const key = norm(s.key);
        if (!key) return;
        products[key] = {
          key,
          name: s.name || key,
          message: s.message || '',
          // The block's name wins: Shopify titles are not localized (e.g. "Magnesium Complete" on the ES store).
          title: s.name || s.product?.title || key,
          url: s.product?.url || '',
          image: s.product?.image || '',
          variantId: s.product?.variantId || null,
          sellingPlanId: s.product?.sellingPlanId || null,
          available: s.product?.available !== false,
          discountCodes: s.product?.discountCodes || '',
        };
        return;
      }
      // Entry link (?objetivo=digestion): its own intro texts and preset answers.
      if (block.type === 'entry') {
        const value = norm(s.value);
        if (value && !s.hide_step) {
          entries.push({ value, eyebrow: s.eyebrow || '', heading: s.heading || '', text: s.text || '', btn: s.btn || '', presets: parsePresets(s.preset), skip: Boolean(s.skip_preset) });
        }
        return;
      }
      if (block.type === 'message') {
        if (s.product_key && s.value && s.message) {
          messages.push({ field: norm(s.field || 'goals'), value: norm(s.value), product: norm(s.product_key), message: s.message });
        }
        return;
      }
      if (!STEP_TYPES.includes(block.type)) return;
      if (s.hide_step) return;
      // V2-style consent: no consent screen; agreement is the text under the email button.
      if (block.type === 'consent' && consentMode === 'implied') return;

      steps.push({
        id: 'step_' + block.id,
        blockId: block.id,
        type: block.type,
        // A Consent screen must store its tick, or answers could never be sent.
        field: norm(s.payload_field) || (block.type === 'consent' ? 'consent_' + norm(block.id) : ''),
        conditions: [
          parseCondition(s.cond1_field, s.cond1_operator, s.cond1_value),
          parseCondition(s.cond2_field, s.cond2_operator, s.cond2_value),
        ].filter(Boolean),
        followUpOf: norm(s.follow_up_of),
        followUpValue: norm(s.follow_up_value),
        options: parseOptions(s.options),
        maxSelections: parseInt(s.max_selections, 10) || 0,
        // Ranked multi-choice: choices are numbered in tap order (importance); only the
        // first `countedSelections` drive follow-ups and points, the rest are only stored.
        ranked: Boolean(s.ranked),
        countedSelections: parseInt(s.counted_selections, 10) || 0,
        shuffle: Boolean(s.shuffle_options),
        pinned: list(s.pinned_values),
        exclusive: list(s.exclusive_values),
        optional: Boolean(s.optional),
        s,
      });
    });

    const keys = Object.keys(products);
    const tieOrder = list(config.settings?.tieOrder).filter((k) => keys.includes(k));
    keys.forEach((k) => tieOrder.includes(k) || tieOrder.push(k));
    const fallback = norm(config.settings?.fallbackProduct);

    return {
      steps,
      products,
      messages,
      entries,
      tieOrder,
      minPoints: Number(config.settings?.minPoints ?? 3),
      // Complements can ask for more than the main product; defaults to the same threshold.
      complementMinPoints: Number(config.settings?.complementMinPoints ?? config.settings?.minPoints ?? 3),
      maxProducts: Math.max(1, Number(config.settings?.maxProducts ?? 3)),
      fallback: keys.includes(fallback) ? fallback : tieOrder[0],
      consentMode,
    };
  }

  // ==================== Sequence & scoring (pure) ====================

  /** Answers are stored by payload field: a string, or an array (tap order) for multi-choice. */
  function valuesOf(answer) {
    if (Array.isArray(answer)) return answer.map(norm);
    return answer == null || answer === '' ? [] : [norm(answer)];
  }

  /**
   * The answers the flow and the scoring see: a multi-choice answer is cut to its step's
   * "Choices that count" (first ones tapped). The full list is still stored and sent to Klaviyo.
   */
  function countedAnswers(model, answers) {
    const out = Object.assign({}, answers);
    model.steps.forEach((step) => {
      if (step.countedSelections && step.field && Array.isArray(out[step.field])) {
        out[step.field] = out[step.field].slice(0, step.countedSelections);
      }
    });
    return out;
  }

  function conditionPasses(cond, answers) {
    const given = valuesOf(answers[cond.field]);
    const wanted = list(cond.value);
    switch (cond.operator) {
      case 'equals':
        return given.some((v) => wanted.includes(v));
      case 'not_equals':
        return !given.some((v) => wanted.includes(v));
      case 'exists':
        return given.length > 0;
      case 'not_exists':
        return given.length === 0;
      case 'less_than':
        return given.length > 0 && parseFloat(given[0]) < parseFloat(cond.value);
      case 'greater_than':
        return given.length > 0 && parseFloat(given[0]) > parseFloat(cond.value);
      default:
        return true;
    }
  }

  const stepVisible = (step, answers) => step.conditions.every((c) => conditionPasses(c, answers));

  /**
   * Ordered steps for the current answers. Follow-up steps are placed right
   * after their parent, in the order the parent's options were chosen. An
   * "end" or "result" step stops the sequence.
   * opts.answered: also the questions an entry link answered and skips (they count for
   * scoring and Klaviyo, but aren't screens).
   */
  function buildSequence(model, allAnswers, opts) {
    const withSkipped = Boolean(opts?.answered);
    const answers = countedAnswers(model, allAnswers);
    const seq = [];
    // Conditions only see answers from steps already on this path, so an answer
    // left behind on a step that is no longer shown can't change the flow.
    const active = {};
    const include = (step) => {
      seq.push(step);
      if (step.field && answers[step.field] !== undefined) active[step.field] = answers[step.field];
    };

    for (const step of model.steps) {
      if (step.followUpOf) continue;
      // "Only with an entry link" screens (an angle's intro) are skipped for everyone else.
      if (step.s?.entry_only && !model.entryActive) continue;
      if (!stepVisible(step, active)) continue;
      if (!withSkipped && model.skipFields?.has(step.field) && answers[step.field] !== undefined) {
        // Answered by an entry link: not asked, but it still counts and brings its follow-ups.
        active[step.field] = answers[step.field];
      } else {
        include(step);
        if (TERMINAL_TYPES.includes(step.type)) return seq;
      }

      // One level only: follow-ups of follow-ups are not supported.
      for (const value of step.field ? valuesOf(active[step.field]) : []) {
        for (const f of model.steps) {
          if (f.followUpOf !== step.field || f.followUpValue !== value || !stepVisible(f, active)) continue;
          include(f);
          if (TERMINAL_TYPES.includes(f.type)) return seq;
        }
      }
    }
    return seq;
  }

  function addPoints(scores, points) {
    Object.entries(points).forEach(([key, n]) => {
      if (key in scores) scores[key] += n;
    });
  }

  function recommend(model, allAnswers) {
    const answers = countedAnswers(model, allAnswers);
    const scores = {};
    Object.keys(model.products).forEach((k) => (scores[k] = 0));
    const excluded = new Set();
    const notices = [];
    const tipCandidates = [];

    // Only answers on the visitor's current path count (not ones left behind after going back).
    const seq = buildSequence(model, answers, { answered: true });
    seq.forEach((step) => {
      if (!step.options.length || !step.field) return;
      valuesOf(answers[step.field]).forEach((value) => {
        const option = step.options.find((o) => o.value === value);
        if (!option) return;
        addPoints(scores, option.points);
        option.exclude.forEach((k) => excluded.add(k));
        if (option.notice && !notices.includes(option.notice)) notices.push(option.notice);
        if (option.tip) tipCandidates.push({ text: option.tip, product: option.tipProduct });
      });
    });

    // Messages matching the visitor's answers, in step order then tap order.
    const answeredFields = new Set(seq.map((s) => s.field).filter(Boolean));
    const matchedMessages = [];
    seq.forEach((step) => {
      if (!answeredFields.has(step.field)) return;
      valuesOf(answers[step.field]).forEach((value) => {
        model.messages
          .filter((m) => m.field === step.field && m.value === value)
          .forEach((m) => matchedMessages.push(m));
      });
    });

    // Ties: product of the first matching message (e.g. first chosen goal), then the next, then tie order.
    const priority = [];
    matchedMessages.map((m) => m.product).concat(model.tieOrder).forEach((k) => {
      if (k in scores && !priority.includes(k)) priority.push(k);
    });
    const ranked = priority
      .filter((k) => !excluded.has(k))
      .sort((a, b) => scores[b] - scores[a] || priority.indexOf(a) - priority.indexOf(b));

    let main = ranked[0];
    if (main === undefined || scores[main] < model.minPoints) {
      main = excluded.has(model.fallback) ? ranked[0] : model.fallback;
    }

    const complements = ranked
      .filter((k) => k !== main && scores[k] >= model.complementMinPoints)
      .slice(0, model.maxProducts - 1);

    const matching = matchedMessages.find((m) => m.product === main);
    const message = matching ? matching.message : model.products[main]?.message || '';

    // Personal lines from the answers, in question order: only for products in the result,
    // no duplicates, never repeating the main message, at most MAX_TIPS.
    const shown = [main].concat(complements);
    const tips = [];
    tipCandidates.forEach((t) => {
      if (t.product && !shown.includes(t.product)) return;
      if (t.text === message || tips.includes(t.text) || tips.length >= MAX_TIPS) return;
      tips.push(t.text);
    });

    return { main, complements, message, notices, tips, scores };
  }

  /**
   * Option order for one visitor: shuffled (Fisher–Yates) when the step asks for it,
   * with pinned values (e.g. "general", "otro") kept at their original position.
   * Cuts position bias in the answers; ordered scales (age, activity, diet) stay as written.
   */
  function shuffleOptions(options, pinned, rand) {
    const r = rand || Math.random;
    const pin = new Set(pinned || []);
    const free = options.filter((o) => !pin.has(o.value));
    for (let i = free.length - 1; i > 0; i--) {
      const j = Math.floor(r() * (i + 1));
      [free[i], free[j]] = [free[j], free[i]];
    }
    let k = 0;
    return options.map((o) => (pin.has(o.value) ? o : free[k++]));
  }

  // ==================== Klaviyo payload (pure) ====================

  /** True when every Consent screen on the visitor's path was ticked (and there is at least one). */
  function healthConsentGiven(model, answers) {
    const screens = buildSequence(model, answers).filter((st) => st.type === 'consent');
    return screens.length > 0 && screens.every((st) => st.field && answers[st.field] === 'yes');
  }

  /**
   * What the submit may do, per consent mode.
   * explicit: answers only with the health-consent tick (and the section switch on); list only with the marketing tick.
   * implied (V2-style): the text under the button covers both, so answers are sent and everyone is subscribed.
   */
  function consentDecision(model, answers, opts) {
    const o = opts || {};
    if (model.consentMode === 'implied') return { mode: 'implied', sendAnswers: true, marketing: true, healthConsent: false };
    const health = healthConsentGiven(model, answers);
    return { mode: 'explicit', sendAnswers: Boolean(o.sendAnswers) && health, marketing: Boolean(o.marketingTicked), healthConsent: health };
  }

  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  /** The ad's UTM tags from a query string ("?utm_source=facebook&…"); only the ones present. */
  function readUtm(search) {
    const utm = {};
    let params;
    try {
      params = new URLSearchParams(search || '');
    } catch (e) {
      return utm;
    }
    UTM_KEYS.forEach((key) => {
      const value = String(params.get(key) || '').trim().slice(0, 100);
      if (value) utm[key] = value;
    });
    return utm;
  }

  /**
   * Where the visitor started the quiz from: an explicit `?origen=home` on the link wins;
   * else the page on this site they came from (home, product, collection, blog, page, site);
   * else "external" (an ad, an email, a search, typed in). Null when the referrer is the quiz
   * page itself (a reload or a step back), so the session's first source is kept.
   */
  function quizSource(search, referrer, host, path) {
    let explicit = '';
    try {
      explicit = String(new URLSearchParams(search || '').get('origen') || '').trim().toLowerCase();
    } catch (e) {
      explicit = '';
    }
    if (/^[a-z0-9-]{1,30}$/.test(explicit)) return explicit;
    let ref;
    try {
      ref = referrer ? new URL(referrer) : null;
    } catch (e) {
      ref = null;
    }
    const bare = (h) => String(h || '').toLowerCase().replace(/^www\./, '');
    if (!ref || bare(ref.hostname) !== bare(host)) return 'external';
    // Language prefixes (/en, /it, /en-us) don't change which page it was.
    const clean = (p) => String(p || '').replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '').replace(/\/+$/, '') || '/';
    const p = clean(ref.pathname);
    if (path && p === clean(path)) return null;
    if (p === '/') return 'home';
    if (p.startsWith('/products/')) return 'product';
    if (p.startsWith('/collections')) return 'collection';
    if (p.startsWith('/blogs/')) return 'blog';
    if (p.startsWith('/pages/')) return 'page';
    return 'site';
  }

  /**
   * Nutrition consultation: true when an answer on the visitor's path is one of the
   * Result block's "Highlight for answers" ("field:value, field:value"), e.g. blood
   * pressure medication or "prefiero no decirlo" on pregnancy.
   */
  function consultationHighlight(model, answers, list) {
    const wanted = String(list || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => x.split(':').map((y) => norm(y.trim())));
    if (!wanted.length) return false;
    answers = countedAnswers(model, answers);
    return buildSequence(model, answers, { answered: true }).some((step) =>
      step.field ? wanted.some(([f, v]) => f === step.field && valuesOf(answers[step.field]).includes(v)) : false
    );
  }

  /** The entry block for a link value (?objetivo=digestion), or null. */
  function entryFor(model, value) {
    const v = norm(value);
    return (v && model.entries.find((e) => e.value === v)) || null;
  }

  /**
   * An entry's preset answers, only values that exist on their question (a typo in the
   * theme editor must not invent an answer). Multi-choice gets a list.
   */
  function entryAnswers(model, entry) {
    const answers = {};
    (entry?.presets || []).forEach(({ field, values }) => {
      const step = model.steps.find((st) => st.field === field && !st.followUpOf);
      if (!step) return;
      const valid = step.options.length ? values.filter((v) => step.options.some((o) => o.value === v)) : values;
      if (!valid.length) return;
      answers[field] = step.type === 'multi-choice' ? valid.slice(0, step.maxSelections || valid.length) : valid[0];
    });
    return answers;
  }

  /**
   * Which version of the quiz a visitor sees when the page has several (A/B test):
   * forced (?quiz_version=B, for checking), else the one this browser already had (if it
   * still gets traffic), else weighted at random. Weight 0 = paused.
   */
  function pickVersion(versions, opts) {
    const o = opts || {};
    const find = (name) => (name ? versions.findIndex((v) => norm(v.name) === norm(name)) : -1);
    const forced = find(o.forced);
    if (forced >= 0) return forced;
    const stored = find(o.stored);
    if (stored >= 0 && versions[stored].weight > 0) return stored;
    const live = versions.map((v, i) => i).filter((i) => versions[i].weight > 0);
    if (!live.length) return 0;
    const total = live.reduce((a, i) => a + versions[i].weight, 0);
    let r = (o.random == null ? Math.random() : o.random) * total;
    for (const i of live) {
      r -= versions[i].weight;
      if (r < 0) return i;
    }
    return live[live.length - 1];
  }

  /**
   * Version names for the sections on a page: the "Version name" setting, else A, B… by
   * position. A name used twice gets "-2", "-3" so no version disappears and the data
   * stays apart (the theme editor shows a warning to rename them).
   */
  function versionNames(raw) {
    const seen = new Map();
    return raw.map((name, i) => {
      const base = String(name || '').trim() || String.fromCharCode(65 + i);
      const n = (seen.get(norm(base)) || 0) + 1;
      seen.set(norm(base), n);
      return { name: n > 1 ? `${base}-${n}` : base, clash: n > 1 || (!String(name || '').trim() && raw.length > 1) };
    });
  }

  /**
   * The offer line for a recommendation: "Offer text by recommended product" (key||text per
   * line) when the main product has one, else the general Offer text.
   */
  function offerText(offer, mainKey) {
    const line = String(offer?.texts || '')
      .split(/\r?\n/)
      .map((l) => l.split('||'))
      .find(([key, text]) => norm(key) === norm(mainKey) && String(text || '').trim());
    return line ? line[1].trim() : String(offer?.text || '').trim();
  }

  /** Booking link with UTMs, so Calendly shows which bookings came from the quiz. */
  function consultationUrl(base, medium) {
    if (!base) return '';
    try {
      const url = new URL(base);
      if (!url.searchParams.has('utm_source')) url.searchParams.set('utm_source', 'quiz');
      if (!url.searchParams.has('utm_medium')) url.searchParams.set('utm_medium', medium || 'resultado');
      return url.href;
    } catch (e) {
      return base;
    }
  }

  /**
   * Phone in international format (E.164, what Klaviyo and Meta expect), or '' if it
   * doesn't look valid. Spanish numbers can be typed without the prefix ("612 345 678");
   * others need it ("+44 7911 123456" or "0044…").
   */
  function normalizePhone(raw) {
    let v = String(raw || '').replace(/[\s().\- ]/g, '');
    if (v.startsWith('00')) v = '+' + v.slice(2);
    if (!v.startsWith('+')) v = /^[6789]\d{8}$/.test(v) ? '+34' + v : '';
    if (!/^\+[1-9]\d{7,14}$/.test(v)) return '';
    if (v.startsWith('+34') && !/^\+34[6789]\d{8}$/.test(v)) return '';
    return v;
  }

  /**
   * Meta Lead: parameters and advanced-matching data. Never answers or goals; the
   * category is the recommended product, as on any product page's ViewContent.
   * userData is normalised the way Meta asks (trimmed, lowercase); the pixel hashes it.
   */
  function metaLeadData(model, result, opts) {
    const o = opts || {};
    const main = result && model.products[result.main];
    const params = { content_name: 'Quiz' };
    if (main) params.content_category = main.title || result.main;
    const userData = {};
    const email = String(o.email || '').trim().toLowerCase();
    const firstName = String(o.firstName || '').trim().toLowerCase();
    const phone = normalizePhone(o.phone).replace('+', '');
    if (email) userData.em = email;
    if (firstName) userData.fn = firstName;
    if (phone) userData.ph = phone;
    return { params, userData };
  }

  /**
   * Event and profile properties for Klaviyo.
   * Always: the recommendation (names, links, images) so a flow can email it.
   * With opts.sendAnswers: every answer on the visitor's path as `<prefix><field>`
   * (multi-choice as a list, in tap order) plus the product scores. The name goes
   * to first_name, never as a property.
   */
  function klaviyoProperties(model, answers, result, opts) {
    const o = opts || {};
    const prefix = o.prefix == null ? 'quiz_' : o.prefix;
    const abs = o.absoluteUrl || ((u) => u || '');
    const product = (key) => {
      const p = model.products[key] || {};
      return { key, name: p.title || key, url: abs(p.url), image: abs(p.image) };
    };
    const products = [result.main].concat(result.complements).map(product);

    const event = {
      recommended_main: products[0].name,
      recommended_main_key: result.main,
      recommended_main_url: products[0].url,
      recommended_main_image: products[0].image,
      recommended_complements: products.slice(1).map((p) => p.name).join(', '),
      recommended_products: products,
      marketing_consent: Boolean(o.marketing),
      consent_mode: o.consentMode || 'explicit',
      health_consent: Boolean(o.healthConsent),
    };
    const profile = {};
    profile[prefix + 'recommended_main'] = result.main;
    profile[prefix + 'recommended_complements'] = result.complements.slice();

    // Quiz version (A/B test) on the event and the profile; the entry link only with the answers
    // (it names a goal); the offer code so the emails can repeat it.
    if (o.version) event[prefix + 'version'] = profile[prefix + 'version'] = o.version;
    // Where the quiz was started from (home, product, external…): not an answer, always sent.
    if (o.source) event[prefix + 'source'] = profile[prefix + 'source'] = o.source;
    if (o.entry && o.sendAnswers) {
      event[prefix + 'entry'] = profile[prefix + 'entry'] = o.entry;
      // The entry's label (e.g. "Digestión") for the email subject: «Digestión: tu recomendación está lista».
      if (o.entryLabel) event[prefix + 'entry_label'] = o.entryLabel;
    }
    if (o.offerCode) event.offer_code = o.offerCode;

    // Ad that brought the visitor: on the event as utm_*, on the profile as <prefix>utm_* (latest quiz).
    Object.keys(o.utm || {}).forEach((key) => {
      event[key] = o.utm[key];
      profile[prefix + key] = o.utm[key];
    });

    if (o.sendAnswers) {
      const answersOut = {};
      buildSequence(model, answers, { answered: true }).forEach((step) => {
        if (!step.field || step.field === 'name' || answers[step.field] === undefined || answers[step.field] === '') return;
        const value = answers[step.field];
        answersOut[prefix + step.field] = Array.isArray(value) ? value.slice() : value;
        // Ranked: the one marked 1 (most important), e.g. quiz_goals_first.
        if (step.ranked && Array.isArray(value) && value.length) answersOut[prefix + step.field + '_first'] = value[0];
      });
      Object.assign(event, answersOut);
      Object.assign(profile, answersOut);
      event[prefix + 'scores'] = Object.assign({}, result.scores);
    }
    return { event, profile };
  }

  // ==================== DOM ====================

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  class PurchaseFlowV3 {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.ui = config.settings || {};
      this.model = compile(config);
      this.answers = {};
      this.stack = [];
      this.current = null;
      this.result = null;
      this.optionOrder = new Map(); // step id → shuffled options, stable while the visitor goes back and forth
      this.progressValue = 0;
      this.utm = this.readUtmOnce();
      this.source = this.readSourceOnce();
      // A/B test: the version init() picked for this browser (or this section's own name).
      this.version = root.dataset.quizVersion || this.ui.version?.name || '';
      this.offer = this.ui.offer?.code ? this.ui.offer : null;
      this.applyEntry();
      // A history entry per step, so the phone's back gesture (and the Facebook/Instagram
      // in-app browser's) goes back one question instead of leaving with every answer.
      this.historyEnabled = !window.Shopify?.designMode && typeof window.history?.pushState === 'function';

      this.stage = root.querySelector('[data-quiz-stage]');
      this.progress = root.querySelector('[data-quiz-progress]');
      this.backButton = root.querySelector('.back-button');
      this.leaveScreen = root.querySelector('[data-quiz-leave]');

      if (!this.stage || !this.model.steps.length) {
        console.warn('PurchaseFlowV3: no steps configured');
        return;
      }
      this.bindEvents();
      this.show(this.firstStep());
    }

    /**
     * Entry link (?objetivo=digestion, parameter set on the section): the intro takes the
     * entry's texts and its preset answers are filled in; with "Skip the preset question"
     * that question isn't asked (its follow-ups still are).
     */
    applyEntry(forced) {
      const param = this.ui.entryParam || 'objetivo';
      const key = `pleto_quiz_entry:${window.location.pathname}`;
      let value = forced || '';
      if (!value) {
        try {
          const url = new URL(window.location.href);
          value = url.searchParams.get(param) || '';
          if (value) {
            // The link names a goal: keep it out of the address bar, so the Meta Lead and
            // later analytics events don't carry it. Remembered for the tab (reloads).
            window.sessionStorage.setItem(key, value);
            url.searchParams.delete(param);
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
          } else {
            value = window.sessionStorage.getItem(key) || '';
          }
        } catch (e) {
          // Storage or history blocked: the link still works for this page view.
        }
      }
      this.entry = entryFor(this.model, value);
      this.model.entryActive = Boolean(this.entry);
      if (!this.entry) return;
      Object.assign(this.answers, entryAnswers(this.model, this.entry));
      if (this.entry.skip) this.model.skipFields = new Set(this.entry.presets.map((p) => p.field).filter((f) => f in this.answers));
    }

    /** A step's settings, with the entry's texts on the intro (the first content screen). */
    settingsFor(step) {
      if (!this.entry || step.type !== 'content' || step !== this.model.steps.find((st) => st.type === 'content')) return step.s;
      const e = this.entry;
      return Object.assign({}, step.s, e.eyebrow && { eyebrow: e.eyebrow }, e.heading && { heading: e.heading }, e.text && { text: e.text }, e.btn && { btn: e.btn });
    }

    /** The offer line (section → Offer): text with {code} shown as the code. */
    renderOffer(where) {
      const o = this.offer;
      if (!o || !(where === 'email' ? o.onEmail : o.onResult)) return '';
      const raw = offerText(o, (this.result || recommend(this.model, this.answers)).main);
      if (!raw) return '';
      const code = `<strong class="pleto-quiz__offer-code">${escapeHtml(o.code)}</strong>`;
      const text = escapeHtml(raw).replace(/\{code\}/gi, code);
      return `<p class="pleto-quiz__offer pleto-quiz__offer--${where}">${text}</p>`;
    }

    /** Product link: through /discount/CODE when the offer applies itself, so the PDP and checkout carry it. */
    offerUrl(url) {
      if (!url || !this.offer?.apply) return url;
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      return `${root}discount/${encodeURIComponent(this.offer.code)}?redirect=${encodeURIComponent(url)}`;
    }

    firstStep() {
      return buildSequence(this.model, this.answers)[0] || this.model.steps[0];
    }

    /** UTM tags of the landing URL, kept for the session so a reload without them keeps the ad's. */
    readUtmOnce() {
      const fromUrl = readUtm(window.location.search);
      try {
        if (Object.keys(fromUrl).length) window.sessionStorage.setItem('pleto_quiz_utm', JSON.stringify(fromUrl));
        else return JSON.parse(window.sessionStorage.getItem('pleto_quiz_utm') || '{}') || {};
      } catch (e) {
        // Storage blocked (private mode, in-app browsers): the URL's tags are enough.
      }
      return fromUrl;
    }

    /**
     * Where this session started the quiz (quizSource), kept for the session per page so a
     * reload or a step back keeps it. `?origen=` is removed from the address bar after reading.
     */
    readSourceOnce() {
      const key = `pleto_quiz_source:${window.location.pathname}`;
      const found = quizSource(window.location.search, document.referrer, window.location.hostname, window.location.pathname);
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('origen')) {
          url.searchParams.delete('origen');
          window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        }
      } catch (e) {
        // Old browsers: the parameter just stays in the address bar.
      }
      try {
        if (found) window.sessionStorage.setItem(key, found);
        return found || window.sessionStorage.getItem(key) || 'reload';
      } catch (e) {
        return found || 'unknown';
      }
    }

    bindEvents() {
      this.stage.addEventListener('click', (e) => {
        const consult = e.target.closest('[data-quiz-consult]');
        if (consult) this.trackConsultation(consult.dataset.quizConsult === 'highlighted');
        // The result's product links leave the page: report the tap first, then follow the link a
        // moment later so the event isn't lost (a new-tab click goes straight on).
        const productLink = e.target.closest('a[data-quiz-product]');
        if (productLink) {
          this.trackResultClick('view_product', productLink.dataset.quizProduct, productLink.dataset.quizMain === 'true');
          const newTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1 || productLink.target === '_blank';
          if (!newTab && productLink.href) {
            e.preventDefault();
            setTimeout(() => { window.location.href = productLink.href; }, 250);
          }
          return;
        }
        const target = e.target.closest('[data-action]');
        if (!target) return;
        if (!target.classList.contains('is-disabled')) this.handleAction(target.dataset.action, target);
        else if (target.dataset.action === 'consent') this.stage.querySelector('[data-quiz-required]')?.classList.remove('hidden');
      });
      this.stage.addEventListener('change', (e) => {
        if (e.target.name === 'consent') {
          this.stage.querySelector('[data-action="consent"]')?.classList.toggle('is-disabled', !e.target.checked);
          if (e.target.checked) this.stage.querySelector('[data-quiz-required]')?.classList.add('hidden');
        }
      });
      this.stage.addEventListener('input', () => this.stage.querySelector('.pleto-quiz__error')?.classList.add('hidden'));
      this.stage.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.current.type === 'email') this.submitEmail(e.target);
        else if (this.current.type === 'input') this.submitInput(e.target.elements.answer);
      });

      this.backButton?.addEventListener('click', () => this.back());
      this.backButton?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        this.back();
      });
      if (this.historyEnabled) window.addEventListener('popstate', (e) => this.onPopState(e));
      // X / logo: first tap asks (leave screen); a second tap there means "yes, leave".
      this.root.querySelectorAll('[data-quiz-close]').forEach((btn) =>
        btn.addEventListener('click', () => {
          if (this.isLeaveScreenOpen()) window.location.href = this.ui.homeUrl || '/';
          else this.toggleLeaveScreen(true);
        })
      );
      this.root.querySelector('[data-quiz-stay]')?.addEventListener('click', () => this.toggleLeaveScreen(false));
    }

    handleAction(action, target) {
      switch (action) {
        case 'next':
          this.next();
          break;
        case 'consent':
          this.answerAndNext('yes');
          break;
        case 'skip':
          this.answerAndNext('');
          break;
        case 'select':
          this.answerAndNext(target.dataset.value);
          break;
        case 'toggle':
          this.toggleOption(target.dataset.value);
          break;
        case 'continue':
          if (valuesOf(this.answers[this.current.field]).length) this.next();
          break;
        case 'add-to-cart':
          if (!target.classList.contains('is-loading')) this.trackResultClick('add_to_cart', target.dataset.product, true);
          this.addToCart(target);
          break;
      }
    }

    submitInput(input) {
      const value = input.value.trim();
      if (!value && !this.current.optional) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      this.answerAndNext(value);
    }

    answerAndNext(value) {
      if (this.current.field) this.answers[this.current.field] = value;
      this.next();
    }

    next() {
      const seq = buildSequence(this.model, this.answers);
      const index = seq.findIndex((s) => s.id === this.current.id);
      const nextStep = seq[index + 1];
      if (!nextStep) return;
      this.stack.push(this.current);
      if (this.historyEnabled) {
        try {
          window.history.pushState({ pletoQuiz: this.stack.length }, '');
          this.historyPushed = true;
        } catch (e) {
          this.historyEnabled = false;
        }
      }
      this.show(nextStep, 'forward');
    }

    back() {
      if (this.isLeaveScreenOpen()) {
        this.toggleLeaveScreen(false);
        return;
      }
      if (this.submitting || !this.stack.length || TERMINAL_TYPES.includes(this.current.type)) return;
      // Our entry is on top: go back through the browser so its history stays in step (onPopState shows the screen).
      if (this.historyPushed && window.history.state?.pletoQuiz === this.stack.length) {
        window.history.back();
        return;
      }
      this.show(this.stack.pop(), 'back');
    }

    /** Browser back/forward over the quiz's own entries; depth = steps behind the current one. */
    onPopState(e) {
      if (!this.historyPushed || !this.current) return;
      const depth = typeof e.state?.pletoQuiz === 'number' ? e.state.pletoQuiz : 0;
      if (TERMINAL_TYPES.includes(this.current.type)) {
        // Finished (or an exit screen): the questions can't be reopened. Skip the rest of
        // the quiz's entries in one go so the next back leaves the page.
        if (depth > 0) window.history.go(-depth);
        return;
      }
      if (depth > this.stack.length) {
        // Forward button: those screens can't be redone, undo the jump.
        window.history.go(this.stack.length - depth);
        return;
      }
      if (this.isLeaveScreenOpen() || this.submitting) {
        // Back on the leave screen means stay; put back the entry the browser just dropped.
        if (this.isLeaveScreenOpen()) this.toggleLeaveScreen(false);
        if (depth < this.stack.length) window.history.pushState({ pletoQuiz: this.stack.length }, '');
        return;
      }
      let step = null;
      while (this.stack.length > depth) step = this.stack.pop();
      if (step) this.show(step, 'back');
    }

    isLeaveScreenOpen() {
      return Boolean(this.leaveScreen && !this.leaveScreen.classList.contains('hidden'));
    }

    show(step, direction) {
      this.current = step;
      this.stage.innerHTML = this.render(step);
      this.toggleLeaveScreen(false, true);

      const terminal = TERMINAL_TYPES.includes(step.type);
      if (this.backButton) this.backButton.style.visibility = this.stack.length && !terminal ? 'visible' : 'hidden';
      this.updateProgress(step, direction || 'forward');
      this.trackStep(step);

      const input = this.stage.querySelector('input[type="text"], input[type="email"]');
      if (input && !window.Shopify?.designMode) setTimeout(() => input.focus(), 100);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    /** Theme editor: jump straight to the selected block's screen. */
    preview(blockId) {
      // An Entry link block: show the intro as that link opens it.
      const entryBlock = (this.config.blocks || []).find((b) => b.id === blockId && b.type === 'entry');
      if (entryBlock) {
        this.answers = {};
        this.model.skipFields = null;
        this.applyEntry(entryBlock.settings?.value);
        this.entryPreview = true;
        this.stack = [];
        this.progressValue = 0;
        this.show(this.firstStep());
        return;
      }
      const step = this.model.steps.find((s) => s.blockId === blockId);
      if (!step) return;
      if (this.entryPreview) {
        // Back from an Entry link preview: the plain quiz again.
        this.entry = null;
        this.model.entryActive = false;
        this.model.skipFields = null;
        this.answers = {};
        this.entryPreview = false;
      }
      this.stack = [];
      this.progressValue = 0;
      this.show(step);
    }

    /**
     * The sequence grows when goals add follow-ups, so the plain ratio would drop on the
     * next screen. Going forward the bar never moves back; Back may lower it. 'stay' keeps it.
     */
    updateProgress(step, direction) {
      if (!this.progress) return;
      const seq = buildSequence(this.model, this.answers).filter((s) => s.type !== 'content' && !TERMINAL_TYPES.includes(s.type));
      const index = seq.findIndex((s) => s.id === step.id);
      const hide = index < 0;
      let value = hide ? 0 : (index + 1) / (seq.length + 1);
      if (!hide && direction === 'forward') value = Math.max(value, this.progressValue);
      if (!hide && direction === 'stay') value = this.progressValue || value;
      if (!hide) this.progressValue = value;
      this.progress.style.setProperty('--quiz-progress', String(value));
      this.progress.classList.toggle('hidden', hide);
    }

    toggleOption(value) {
      const step = this.current;
      const max = step.maxSelections || Infinity;
      let chosen = valuesOf(this.answers[step.field]);
      if (chosen.includes(value)) {
        chosen = chosen.filter((v) => v !== value);
      } else if (step.exclusive.includes(value)) {
        chosen = [value];
      } else {
        chosen = chosen.filter((v) => !step.exclusive.includes(v));
        if (chosen.length >= max) return;
        chosen.push(value);
      }
      this.answers[step.field] = chosen;

      this.stage.querySelectorAll('[data-action="toggle"]').forEach((btn) => {
        const selected = chosen.includes(btn.dataset.value);
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', String(selected));
        btn.classList.toggle('is-disabled', !selected && !step.exclusive.includes(btn.dataset.value) && chosen.length >= max);
        // Ranked: number in tap order (1 = most important); untapping renumbers the rest.
        if (step.ranked) btn.querySelector('.pleto-quiz__rank').textContent = selected ? chosen.indexOf(btn.dataset.value) + 1 : '';
      });
      this.stage.querySelector('[data-action="continue"]')?.classList.toggle('is-disabled', chosen.length === 0);
      const counter = this.stage.querySelector('[data-quiz-count]');
      if (counter) counter.textContent = chosen.length;
    }

    // ==================== Rendering ====================

    render(step) {
      const s = this.settingsFor(step);
      const eyebrow = s.eyebrow ? `<p class="pleto-quiz__eyebrow">${escapeHtml(this.interpolate(s.eyebrow))}</p>` : '';
      const heading = s.heading ? `<h2 class="pleto-quiz__title">${this.interpolate(s.heading)}</h2>` : '';
      const text = s.text ? `<div class="pleto-quiz__text">${this.interpolate(s.text)}</div>` : '';
      const head = eyebrow + heading + text;
      const wrap = (inner) =>
        `<div class="pleto-quiz__step pleto-quiz__step--${step.type}" data-step-type="${step.type}" data-step-name="${escapeHtml(step.field || step.type)}">${inner}</div>`;
      const cta = (label, action, disabled, type) =>
        `<button type="${type || 'button'}" class="pleto-quiz__cta${disabled ? ' is-disabled' : ''}"${action ? ` data-action="${action}"` : ''}>${escapeHtml(label)}</button>`;
      const privacy = `<a href="${escapeHtml(this.ui.privacyUrl || '#')}" target="_blank" rel="noopener">${escapeHtml(this.ui.privacyLabel)}</a>`;

      switch (step.type) {
        case 'content':
          return wrap(`${head}${s.btn ? `<div class="pleto-quiz__actions">${cta(s.btn, 'next')}</div>` : ''}${this.renderTrust(s)}`);

        case 'input':
          return wrap(`${head}
            <form class="pleto-quiz__form" novalidate>
              <input type="text" name="answer" class="pleto-quiz__input" placeholder="${escapeHtml(s.placeholder)}" autocomplete="${step.field === 'name' ? 'given-name' : 'off'}" enterkeyhint="next" maxlength="80" value="${escapeHtml(this.answers[step.field] || '')}">
              <div class="pleto-quiz__actions pleto-quiz__actions--sticky">
                ${cta(s.btn, '', false, 'submit')}
                ${step.optional ? `<button type="button" class="pleto-quiz__link" data-action="skip">${escapeHtml(this.ui.skipLabel)}</button>` : ''}
              </div>
            </form>`);

        case 'single-choice': {
          const current = valuesOf(this.answers[step.field])[0];
          const options = this.orderedOptions(step).map((o) => `
            <button type="button" class="pleto-quiz__option${current === o.value ? ' is-selected' : ''}" data-action="select" data-value="${escapeHtml(o.value)}">
              <span class="pleto-quiz__option-label">${escapeHtml(o.label)}</span><span class="pleto-quiz__radio" aria-hidden="true"></span>
            </button>`).join('');
          return wrap(`${head}<div class="pleto-quiz__options">${options}</div>`);
        }

        case 'multi-choice': {
          const chosen = valuesOf(this.answers[step.field]);
          const max = step.maxSelections || Infinity;
          const options = this.orderedOptions(step).map((o) => {
            const selected = chosen.includes(o.value);
            const disabled = !selected && !step.exclusive.includes(o.value) && chosen.length >= max;
            const rank = step.ranked && selected ? chosen.indexOf(o.value) + 1 : '';
            return `<button type="button" class="pleto-quiz__option pleto-quiz__option--check${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}" aria-pressed="${selected}" data-action="toggle" data-value="${escapeHtml(o.value)}">
              <span class="pleto-quiz__option-label">${escapeHtml(o.label)}</span><span class="pleto-quiz__box${step.ranked ? ' pleto-quiz__rank' : ''}" aria-hidden="true">${rank}</span>
            </button>`;
          }).join('');
          const hint = s.hint
            ? `<p class="pleto-quiz__hint">${escapeHtml(s.hint)}${step.maxSelections ? ` · <span data-quiz-count>${chosen.length}</span>/${step.maxSelections}` : ''}</p>`
            : '';
          return wrap(`${head}${hint}
            <div class="pleto-quiz__options">${options}</div>
            <div class="pleto-quiz__actions pleto-quiz__actions--sticky">${cta(s.btn, 'continue', !chosen.length)}</div>`);
        }

        case 'consent': {
          const given = Boolean(this.answers[step.field]);
          return wrap(`${head}
            <label class="pleto-quiz__check">
              <input type="checkbox" name="consent"${given ? ' checked' : ''}><span class="pleto-quiz__box" aria-hidden="true"></span>
              <span>${escapeHtml(s.checkbox_label)} ${privacy}</span>
            </label>
            <p class="pleto-quiz__error hidden" role="alert" data-quiz-required>${escapeHtml(s.required_hint || 'Marca la casilla para continuar.')}</p>
            <div class="pleto-quiz__actions pleto-quiz__actions--sticky">${cta(s.btn, 'consent', !given)}</div>`);
        }

        case 'email': {
          const benefits = String(s.benefits || '').split('\n').map((l) => l.trim()).filter(Boolean);
          const box = benefits.length
            ? `<div class="pleto-quiz__benefits">${s.benefits_title ? `<p class="pleto-quiz__benefits-title">${escapeHtml(s.benefits_title)}</p>` : ''}<ol>${benefits.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ol></div>`
            : '';
          return wrap(`${head}${box}${this.renderOffer('email')}
            <form class="pleto-quiz__form pleto-quiz__email" novalidate>
              ${s.ask_name
                ? `<input type="text" name="name" class="pleto-quiz__input pleto-quiz__name" placeholder="${escapeHtml(s.name_placeholder || 'Tu nombre')}" autocomplete="given-name" enterkeyhint="next" maxlength="80" value="${escapeHtml(this.answers.name || '')}"${s.name_required ? ' required' : ''}>`
                : ''}
              <input type="email" name="email" class="pleto-quiz__input" placeholder="${escapeHtml(s.placeholder)}" autocomplete="email" inputmode="email" enterkeyhint="${s.ask_phone ? 'next' : 'send'}" required>
              ${s.ask_phone
                ? `<input type="tel" name="phone" class="pleto-quiz__input pleto-quiz__phone" placeholder="${escapeHtml(s.phone_placeholder || 'Tu teléfono (opcional)')}" autocomplete="tel" inputmode="tel" enterkeyhint="send" maxlength="20"${s.phone_required ? ' required' : ''}>`
                : ''}
              <p class="pleto-quiz__error hidden" role="alert"></p>
              ${this.model.consentMode === 'implied'
                ? ''
                : `<label class="pleto-quiz__check">
                <input type="checkbox" name="marketing"><span class="pleto-quiz__box" aria-hidden="true"></span>
                <span>${escapeHtml(s.checkbox_label)}</span>
              </label>
              ${s.ask_phone && s.sms_subscribe
                ? `<label class="pleto-quiz__check">
                <input type="checkbox" name="sms"><span class="pleto-quiz__box" aria-hidden="true"></span>
                <span>${escapeHtml(s.sms_checkbox_label || 'Quiero recibir comunicaciones comerciales de Pleto por SMS.')}</span>
              </label>`
                : ''}`}
              <div class="pleto-quiz__actions pleto-quiz__actions--sticky">
                <button type="submit" class="pleto-quiz__cta pleto-quiz__submit"><span class="pleto-quiz__submit-label">${escapeHtml(s.btn)}</span><span class="pleto-quiz__spinner" aria-hidden="true"></span></button>
              </div>
              ${this.model.consentMode === 'implied' && s.implied_text
                ? `<div class="pleto-quiz__legal pleto-quiz__legal--implied">${s.implied_text}</div>`
                : `<p class="pleto-quiz__legal">${privacy}</p>`}
            </form>`);
        }

        case 'end':
          return wrap(`${head}
            ${s.btn ? `<div class="pleto-quiz__actions"><a class="pleto-quiz__cta" href="${escapeHtml(this.ui.homeUrl || '/')}">${escapeHtml(s.btn)}</a></div>` : ''}`);

        case 'result':
          return wrap(this.renderResult(s));
      }
      return '';
    }

    /**
     * Trust line under the intro button. With "Show review rating" on: five stars and
     * the block's Rating text if filled (ad hoc), else the live review score.
     */
    orderedOptions(step) {
      if (!step.shuffle) return step.options;
      if (!this.optionOrder.has(step.id)) this.optionOrder.set(step.id, shuffleOptions(step.options, step.pinned));
      return this.optionOrder.get(step.id);
    }

    renderTrust(s) {
      const live = s.show_rating ? this.ui.rating : null;
      const adhoc = s.show_rating ? String(s.rating_text || '').trim() : '';
      if (!live && !adhoc && !s.trust_text) return '';
      const locale = window.Shopify?.locale || 'es';
      const fmt = (n, digits) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n);
      let stars = '';
      if (adhoc) {
        stars = `<span class="pleto-quiz__rating" role="img" aria-label="${escapeHtml(adhoc)}">
             <span class="pleto-quiz__stars" style="--quiz-rating: 1" aria-hidden="true">★★★★★</span>
             <span class="pleto-quiz__rating-score">${escapeHtml(adhoc)}</span>
           </span>`;
      } else if (live) {
        const scale = Number(live.scale) || 5;
        stars = `<span class="pleto-quiz__rating" role="img" aria-label="${escapeHtml(fmt(live.score, 1))}/${fmt(scale, 0)}">
             <span class="pleto-quiz__stars" style="--quiz-rating: ${Math.max(0, Math.min(1, live.score / scale))}" aria-hidden="true">★★★★★</span>
             <span class="pleto-quiz__rating-score">${escapeHtml(fmt(live.score, 1))}/${fmt(scale, 0)}</span>
             <span class="pleto-quiz__rating-count">· ${escapeHtml(fmt(live.count, 0))} ${escapeHtml(this.ui.ratingLabel || '')}</span>
           </span>`;
      }
      return `<div class="pleto-quiz__trust">${stars}${s.trust_text ? `<span class="pleto-quiz__trust-text">${escapeHtml(s.trust_text)}</span>` : ''}</div>`;
    }

    renderResult(s) {
      const r = this.result || recommend(this.model, this.answers);
      const name = this.firstName();
      const heading = name && s.heading ? this.interpolate(s.heading) : s.heading_no_name || '';
      const complements = r.complements.length
        ? `<p class="pleto-quiz__eyebrow pleto-quiz__eyebrow--section">${escapeHtml(s.complements_heading)}</p>
           <div class="pleto-quiz__complements">${r.complements.map((k) => this.renderProduct(k, s, false)).join('')}</div>`
        : '';
      const notices = r.notices.length
        ? `<div class="pleto-quiz__notices"><p class="pleto-quiz__eyebrow">${escapeHtml(s.notices_heading || 'Importante')}</p><ul>${r.notices.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul></div>`
        : '';

      return `${s.eyebrow ? `<p class="pleto-quiz__eyebrow">${escapeHtml(s.eyebrow)}</p>` : ''}
        <h2 class="pleto-quiz__title">${heading}</h2>
        <p class="pleto-quiz__text pleto-quiz__message">${escapeHtml(r.message)}</p>
        ${r.tips && r.tips.length
          ? `<div class="pleto-quiz__tips"><p class="pleto-quiz__eyebrow">${escapeHtml(s.tips_heading || 'Según tus respuestas')}</p><ul>${r.tips.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}</ul></div>`
          : ''}
        ${this.renderOffer('result')}
        ${this.renderProduct(r.main, s, true)}
        ${complements}
        ${this.renderConsultation(s)}
        ${notices}
        ${s.text ? `<div class="pleto-quiz__disclaimer">${s.text}</div>` : ''}`;
    }

    /**
     * Free nutrition consultation (Result block → Consultation): benefits and an "Agendar
     * cita" link for everyone; when an answer is in "Highlight for answers", an extra line
     * recommends talking to a nutritionist first.
     */
    renderConsultation(s) {
      if (s.consult_show === false || !s.consult_title) return '';
      const highlight = consultationHighlight(this.model, this.answers, s.consult_highlight_values);
      const lines = String(s.consult_text || '').split('\n').map((l) => l.trim()).filter(Boolean);
      const url = consultationUrl(s.consult_url, 'resultado');
      return `<div class="pleto-quiz__consult${highlight ? ' is-highlighted' : ''}">
          ${s.consult_eyebrow ? `<p class="pleto-quiz__eyebrow">${escapeHtml(s.consult_eyebrow)}</p>` : ''}
          <p class="pleto-quiz__consult-title">${escapeHtml(s.consult_title)}</p>
          ${highlight && s.consult_highlight_text ? `<p class="pleto-quiz__consult-note">${escapeHtml(s.consult_highlight_text)}</p>` : ''}
          ${lines.length ? `<div class="pleto-quiz__tips"><ul>${lines.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul></div>` : ''}
          ${url && s.consult_btn
            ? `<a class="pleto-quiz__cta pleto-quiz__consult-cta" href="${escapeHtml(url)}" target="_blank" rel="noopener" data-quiz-consult="${highlight ? 'highlighted' : 'standard'}">${escapeHtml(s.consult_btn)}</a>`
            : ''}
        </div>`;
    }

    renderProduct(key, s, isMain) {
      const p = this.model.products[key];
      if (!p) return '';
      const image = p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" width="480" height="480" loading="lazy">` : '';
      if (isMain) {
        return `<div class="pleto-quiz__product">
            ${s.badge ? `<span class="pleto-quiz__badge">${escapeHtml(s.badge)}</span>` : ''}
            <div class="pleto-quiz__product-media">${image}</div>
            <span class="pleto-quiz__product-title">${escapeHtml(p.title)}</span>
            <div class="pleto-quiz__product-actions">
              ${p.variantId && p.available && s.atc_label
                ? `<button type="button" class="pleto-quiz__cta pleto-quiz__atc" data-action="add-to-cart" data-product="${escapeHtml(key)}"><span class="pleto-quiz__atc-label">${escapeHtml(s.atc_label)}</span><span class="pleto-quiz__spinner" aria-hidden="true"></span></button>`
                : ''}
              ${p.url ? `<a class="${p.variantId && p.available && s.atc_label ? 'pleto-quiz__link' : 'pleto-quiz__cta'}" href="${escapeHtml(this.offerUrl(p.url))}" data-quiz-product="${escapeHtml(key)}" data-quiz-main="true">${escapeHtml(s.btn)}</a>` : ''}
            </div>
          </div>`;
      }
      return `<a class="pleto-quiz__mini"${p.url ? ` href="${escapeHtml(this.offerUrl(p.url))}" data-quiz-product="${escapeHtml(key)}" data-quiz-main="false"` : ''}>
          <span class="pleto-quiz__mini-media">${image}</span>
          <span class="pleto-quiz__mini-info"><span class="pleto-quiz__product-title">${escapeHtml(p.title)}</span></span>
          <span class="pleto-quiz__mini-arrow" aria-hidden="true">→</span>
        </a>`;
    }

    /**
     * Add the product to the cart the way the PDP v3 CTA does: first available variant
     * with its first selling plan, merge the discount codes, open the cart drawer.
     * Falls back to /cart when the drawer isn't on the page.
     */
    async addToCart(button) {
      const p = this.model.products[button.dataset.product];
      const s = this.current.s;
      if (!p?.variantId || button.classList.contains('is-loading')) return;
      const label = button.querySelector('.pleto-quiz__atc-label');
      button.classList.add('is-loading');

      const item = { id: Number(p.variantId), quantity: 1 };
      if (p.sellingPlanId) item.selling_plan = Number(p.sellingPlanId);
      const codes = String(p.discountCodes || '').split(',').map((c) => c.trim()).filter(Boolean);
      if (this.offer?.apply && !codes.some((c) => c.toLowerCase() === this.offer.code.toLowerCase())) codes.push(this.offer.code);
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const post = (path, body) =>
        fetch(root + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
          .then((res) => { if (!res.ok) throw new Error(`${path} ${res.status}`); return res.json(); });

      try {
        const drawer = window.PletoCartDrawer && document.querySelector('cart-drawer-v2') ? window.PletoCartDrawer : null;
        if (drawer) await drawer.addItems([item]);
        else await post('cart/add.js', { items: [item] });

        if (codes.length) {
          try {
            const cart = await (await fetch(root + 'cart.js')).json();
            const merged = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
            const missing = codes.filter((c) => !merged.some((m) => m.toLowerCase() === c.toLowerCase()));
            if (missing.length) {
              const discount = merged.concat(missing).join(',');
              if (drawer) await drawer.updateCart({ discount });
              else await post('cart/update.js', { discount });
            }
          } catch (e) {
            console.warn('PurchaseFlowV3: discount codes', e);
          }
        }

        await this.tagCart(this.result || recommend(this.model, this.answers));
        if (drawer) {
          label.textContent = s.atc_added || label.textContent;
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else {
          window.location.href = root + 'cart';
        }
      } catch (e) {
        console.warn('PurchaseFlowV3: add to cart failed', e);
        label.textContent = s.atc_error || label.textContent;
      } finally {
        button.classList.remove('is-loading');
      }
    }

    /** [[field]] → answer. Only the name is free text, so it's escaped; option values are slugs. */
    interpolate(text) {
      return String(text).replace(/\[\[([\w-]+)\]\]/g, (match, field) => {
        if (norm(field) === 'name') return escapeHtml(this.firstName());
        const value = this.answers[norm(field)];
        return value == null ? '' : escapeHtml(Array.isArray(value) ? value.join(', ') : value);
      });
    }

    firstName() {
      const first = String(this.answers.name || '').trim().split(/\s+/)[0] || '';
      return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
    }

    toggleLeaveScreen(open, silent) {
      if (!this.leaveScreen) return;
      if (open && this.current && TERMINAL_TYPES.includes(this.current.type) && !silent) {
        window.location.href = this.ui.homeUrl || '/';
        return;
      }
      this.leaveScreen.classList.toggle('hidden', !open);
      this.stage.classList.toggle('hidden', open);
      if (this.progress && open) this.progress.classList.add('hidden');
      if (!open && !silent && this.current) this.updateProgress(this.current, 'stay');
    }

    // ==================== Lead submission ====================

    async submitEmail(form) {
      const s = this.current.s;
      const email = form.elements.email.value.trim();
      const decision = consentDecision(this.model, this.answers, {
        sendAnswers: Boolean(this.ui.klaviyo?.sendAnswers),
        marketingTicked: Boolean(form.elements.marketing?.checked),
      });
      const marketing = decision.marketing;
      const error = form.querySelector('.pleto-quiz__error');
      const button = form.querySelector('.pleto-quiz__submit');

      // Name on the email screen (email block's "Ask for first name"), in place of an early name step.
      if (form.elements.name) {
        const name = form.elements.name.value.trim();
        if (s.name_required && !name) {
          error.textContent = s.name_error || 'Escribe tu nombre';
          error.classList.remove('hidden');
          form.elements.name.focus();
          return;
        }
        if (name) this.answers.name = name;
        else delete this.answers.name;
      }
      if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) {
        error.textContent = s.email_error;
        error.classList.remove('hidden');
        return;
      }
      // Optional phone (email block's "Ask for phone number"): empty is fine unless required.
      const phoneRaw = form.elements.phone ? form.elements.phone.value.trim() : '';
      const phone = normalizePhone(phoneRaw);
      if ((phoneRaw && !phone) || (s.phone_required && !phone && form.elements.phone)) {
        error.textContent = s.phone_error || 'Introduce un teléfono válido';
        error.classList.remove('hidden');
        form.elements.phone.focus();
        return;
      }
      // SMS marketing: V2-style mode uses the block's switch (the text under the button must cover SMS);
      // the Consent screen mode needs its SMS tick.
      const sms = Boolean(phone && s.sms_subscribe && (decision.mode === 'implied' || form.elements.sms?.checked));
      if (this.submitting || this.submitted) return;
      this.submitting = true;
      button.classList.add('is-loading');

      this.result = recommend(this.model, this.answers);
      try {
        await this.submitLead(email, decision, this.result);
      } catch (e) {
        console.warn('PurchaseFlowV3: lead submission failed', e);
        this.submitting = false;
        button.classList.remove('is-loading');
        error.textContent = s.submit_error;
        error.classList.remove('hidden');
        return;
      }

      if (phone) this.submitPhone(email, phone, sms);
      this.trackLead(email, phone);
      this.tagCart(this.result);
      document.cookie = `completed_flow=true; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax; Secure`;
      this.submitting = false;
      this.submitted = true;
      button.classList.remove('is-loading');
      button.classList.add('is-disabled');
      this.next();
    }

    async submitLead(email, decision, result) {
      const marketing = decision.marketing;
      const k = this.ui.klaviyo || {};
      const props = klaviyoProperties(this.model, this.answers, result, {
        sendAnswers: decision.sendAnswers,
        prefix: k.prefix,
        marketing,
        consentMode: decision.mode,
        healthConsent: decision.healthConsent,
        utm: this.utm,
        absoluteUrl: (u) => this.absoluteUrl(u),
        version: this.version,
        source: this.source,
        entry: this.entry?.value,
        entryLabel: this.entry?.eyebrow,
        offerCode: this.offer?.code,
      });
      const profile = {
        email,
        first_name: this.firstName() || undefined,
        properties: Object.assign(
          {
            $locale: window.Shopify?.locale || 'es',
            $locale_country: window.Shopify?.country || 'ES',
          },
          props.profile
        ),
      };
      const headers = {
        accept: 'application/vnd.api+json',
        revision: '2025-07-15',
        'content-type': 'application/vnd.api+json',
      };

      // Sent once per email: a retry after a failed subscription must not send a second recommendation email.
      // unique_id lets Klaviyo drop a duplicate that still gets through.
      if (this.eventSentFor !== email) {
        this.eventId = window.crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2);
        const eventResponse = await fetch(`https://a.klaviyo.com/client/events?company_id=${k.companyId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties: props.event,
                unique_id: this.eventId,
                metric: { data: { type: 'metric', attributes: { name: k.eventName } } },
                profile: { data: { type: 'profile', attributes: profile } },
              },
            },
          }),
        });
        if (!eventResponse.ok) throw new Error(`Klaviyo events API returned ${eventResponse.status}`);
        this.eventSentFor = email;
      }

      if (!marketing) return;

      // Marketing consent given: tie this browser to the profile so Klaviyo's onsite
      // events (Viewed Product, Added to Cart → abandoned cart flows) reach it.
      try {
        if (window.klaviyo?.identify) window.klaviyo.identify({ email });
        else if (Array.isArray(window._learnq)) window._learnq.push(['identify', { $email: email }]);
      } catch (e) {
        console.warn('PurchaseFlowV3: klaviyo identify failed', e);
      }

      const subResponse = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${k.companyId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: Object.assign({}, profile, { subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }),
                },
              },
              custom_source: k.source,
            },
            relationships: { list: { data: { type: 'list', id: k.listId } } },
          },
        }),
      });
      if (!subResponse.ok) throw new Error(`Klaviyo subscription API returned ${subResponse.status}`);
    }

    /**
     * Phone on the Klaviyo profile, apart from the lead so a number Klaviyo rejects never
     * loses the lead. With SMS consent: an SMS-marketing subscription to the quiz list;
     * without: the number is only stored on the profile.
     */
    async submitPhone(email, phone, sms) {
      const k = this.ui.klaviyo || {};
      const headers = { accept: 'application/vnd.api+json', revision: '2025-07-15', 'content-type': 'application/vnd.api+json' };
      const profile = { email, phone_number: phone };
      const body = sms
        ? {
            data: {
              type: 'subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: Object.assign({}, profile, { subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } }) } },
                custom_source: k.source,
              },
              relationships: { list: { data: { type: 'list', id: k.listId } } },
            },
          }
        : { data: { type: 'profile', attributes: profile } };
      try {
        const response = await fetch(`https://a.klaviyo.com/client/${sms ? 'subscriptions' : 'profiles'}?company_id=${k.companyId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!response.ok) console.warn(`PurchaseFlowV3: Klaviyo phone ${response.status}`);
      } catch (e) {
        console.warn('PurchaseFlowV3: phone submission failed', e);
      }
    }

    /**
     * A tap on the result's product buttons: «Añadir al carrito» (add_to_cart) or the product link
     * (view_product), with the product key, whether it's the main product, the quiz version and
     * where the quiz was started. GA4 via the Customer events pixel; no answers.
     */
    trackResultClick(cta, productKey, isMain) {
      try {
        window.Shopify?.analytics?.publish?.('quiz_result_click', {
          cta,
          product_key: productKey || '',
          is_main: Boolean(isMain),
          quiz_version: this.version || '',
          quiz_source: this.source || '',
        });
      } catch (e) {
        console.warn('PurchaseFlowV3: result click tracking failed', e);
      }
    }

    /** Anonymous click on "Agendar cita" (GA4 via the Customer events pixel); no answers. */
    trackConsultation(highlighted) {
      try {
        window.Shopify?.analytics?.publish?.('quiz_consultation_click', { highlighted });
      } catch (e) {
        console.warn('PurchaseFlowV3: consultation tracking failed', e);
      }
    }

    /**
     * Funnel: an anonymous Shopify custom event per screen, for a Customer Events pixel
     * to forward to GA4. Only the screen's position and kind; never the question, the
     * answers or the goal (follow-up questions would reveal it).
     */
    trackStep(step) {
      if (window.Shopify?.designMode) return;
      const kinds = { content: 'intro', input: 'question', 'single-choice': 'question', 'multi-choice': 'question', consent: 'consent', email: 'email', result: 'result', end: 'end' };
      const seq = buildSequence(this.model, this.answers);
      const index = Math.max(0, seq.findIndex((s) => s.id === step.id)) + 1;
      this.stepsSeen = (this.stepsSeen || 0) + 1;
      try {
        window.Shopify?.analytics?.publish?.('quiz_step', {
          step_index: index,
          step_kind: kinds[step.type] || 'other',
          screens_seen: this.stepsSeen,
          consent_mode: this.model.consentMode,
          quiz_version: this.version || '',
          quiz_source: this.source || '',
        });
      } catch (e) {
        console.warn('PurchaseFlowV3: step tracking failed', e);
      }
    }

    /**
     * Quiz orders: hidden cart attributes (underscore = not shown at checkout) so orders
     * from a quiz session can be found in Shopify reports. Product keys only, no answers.
     */
    async tagCart(result) {
      if (!result) return;
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const attributes = {
        _quiz: 'v3',
        _quiz_recommendation: result.main,
        _quiz_products: [result.main].concat(result.complements).join(','),
      };
      if (this.version) attributes._quiz_version = this.version;
      if (this.source) attributes._quiz_source = this.source;
      const utm = this.utm || {};
      if (Object.keys(utm).length) attributes._quiz_utm = [utm.utm_source, utm.utm_medium, utm.utm_campaign].map((v) => v || '-').join(' / ');
      try {
        if (window.PletoCartDrawer?.updateCart && document.querySelector('cart-drawer-v2')) await window.PletoCartDrawer.updateCart({ attributes });
        else await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
      } catch (e) {
        console.warn('PurchaseFlowV3: cart tagging failed', e);
      }
    }

    /**
     * Meta "Lead" for the leads campaign (custom conversion "Quiz – Lead"). fbq is the
     * Facebook & Instagram app's pixel; only with marketing (cookie) consent.
     * - Advanced matching: email, first name and phone (if given), so Meta can tie the lead to the ad.
     * - eventID: the Klaviyo event's id, so the browser copy and Meta's server copy merge.
     * - Parameters: "Quiz" and the recommended product only; never answers or goals.
     */
    trackLead(email, phone) {
      try {
        const privacy = window.Shopify?.customerPrivacy;
        const marketingAllowed = typeof privacy?.marketingAllowed === 'function' ? privacy.marketingAllowed() : false;
        if (marketingAllowed && typeof window.fbq === 'function') {
          const lead = metaLeadData(this.model, this.result, { email, firstName: this.firstName(), phone });
          // The pixel the app already loaded; the section setting only if it can't be read.
          const pixelId = window.fbq.getState?.()?.pixels?.[0]?.id || this.ui.metaPixelId;
          if (pixelId && Object.keys(lead.userData).length) window.fbq('init', String(pixelId), lead.userData);
          window.fbq('track', 'Lead', lead.params, this.eventId ? { eventID: this.eventId } : undefined);
        }
        window.Shopify?.analytics?.publish?.('lead_submitted', { quiz_version: this.version || '', quiz_source: this.source || '' });
      } catch (e) {
        console.warn('PurchaseFlowV3: lead tracking failed', e);
      }
    }

    absoluteUrl(path) {
      if (!path) return '';
      try {
        return new URL(path, window.location.origin).href;
      } catch (e) {
        return path;
      }
    }

    // ==================== Debug API ====================

    getAnswers() {
      return JSON.parse(JSON.stringify(this.answers));
    }

    getRecommendation() {
      return recommend(this.model, this.answers);
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { compile, buildSequence, recommend, parseOptions, klaviyoProperties, consentDecision, shuffleOptions, readUtm, quizSource, metaLeadData, normalizePhone, consultationHighlight, consultationUrl, entryFor, entryAnswers, pickVersion, versionNames, offerText };
    return;
  }

  function readConfig(root) {
    try {
      return JSON.parse(root.querySelector('[data-quiz-config]').textContent);
    } catch (e) {
      console.warn('PurchaseFlowV3: invalid config', e);
      return null;
    }
  }

  /**
   * Meta's automatic events (SubscribedButtonClick) send the text of every tapped button,
   * and on this page that's a health answer ("Estrés y nervios", "Ganar fuerza"…). The
   * pixel's server config turns them on even with "Track events automatically" off in
   * Events Manager, so switch them off here for every pixel on the page. The explicit
   * Lead is not affected. The app's pixel can load after this script: retry for 15 s.
   */
  function disableMetaAutoEvents(fallbackPixelId) {
    const done = new Set();
    const apply = () => {
      if (typeof window.fbq !== 'function') return false;
      const ids = (window.fbq.getState?.()?.pixels || []).map((p) => String(p.id));
      if (!ids.length && fallbackPixelId) ids.push(String(fallbackPixelId));
      ids.filter((id) => !done.has(id)).forEach((id) => {
        try {
          window.fbq('set', 'autoConfig', false, id);
          done.add(id);
        } catch (e) {
          console.warn('PurchaseFlowV3: could not turn off Meta automatic events', e);
        }
      });
      return done.size > 0;
    };
    apply();
    let tries = 0;
    const timer = setInterval(() => {
      apply();
      if (++tries >= 60) clearInterval(timer); // keep checking for a late second pixel too
    }, 250);
  }

  /**
   * One quiz section, or several on the same page = versions of an A/B test (section →
   * Version). Each browser keeps its version (localStorage, per page); ?quiz_version=B
   * forces one for checking. The others are hidden. In the theme editor the section being
   * edited is shown.
   */
  function init(scope) {
    const roots = [...document.querySelectorAll('[data-pleto-quiz]')];
    if (!roots.length) return;
    const configs = roots.map(readConfig);
    const names = versionNames(configs.map((c) => c?.settings?.version?.name));
    const versions = roots.map((r, i) => ({ name: names[i].name, weight: Math.max(0, Number(configs[i]?.settings?.version?.weight ?? 50) || 0) }));

    let chosen = 0;
    if (roots.length > 1) {
      if (window.Shopify?.designMode) {
        const current = scope && roots.findIndex((r) => scope === r || scope.contains?.(r) || r.contains?.(scope));
        chosen = current >= 0 ? current : Math.max(0, roots.findIndex((r) => r.closest('.shopify-section')?.style.display !== 'none'));
      } else {
        const key = `pleto_quiz_version:${window.location.pathname}`;
        let stored = null;
        let forced = null;
        try {
          stored = window.localStorage.getItem(key);
        } catch (e) {
          stored = null;
        }
        try {
          forced = new URLSearchParams(window.location.search).get('quiz_version');
        } catch (e) {
          forced = null;
        }
        chosen = pickVersion(versions, { forced, stored });
        if (!forced) {
          try {
            window.localStorage.setItem(key, versions[chosen].name);
          } catch (e) {
            // Storage blocked: a new pick on every visit, still random.
          }
        }
      }
      // The stylesheet hides every quiz section after the first until this runs (no double page).
      roots.forEach((r, i) => {
        const wrapper = r.closest('.shopify-section') || r;
        wrapper.style.display = i === chosen ? 'block' : 'none';
      });
      if (window.Shopify?.designMode) {
        roots.forEach((r, i) => {
          r.querySelector('[data-quiz-version-warning]')?.remove();
          if (!names[i].clash) return;
          const note = document.createElement('p');
          note.setAttribute('data-quiz-version-warning', '');
          note.className = 'pleto-quiz__editor-note';
          note.textContent = `Version "${names[i].name}": give each version its own name (section → Version name). Stored visitors and the data depend on it.`;
          r.prepend(note);
        });
      }
    }

    const root = roots[chosen];
    const config = configs[chosen];
    if (!config) return;
    root.dataset.quizVersion = versions[chosen].name;
    if (!window.Shopify?.designMode && !init.metaDone) {
      disableMetaAutoEvents(config.settings?.metaPixelId);
      init.metaDone = true;
    }
    // One instance per element: re-selecting a version in the theme editor reuses it (no double binding).
    if (!root.pletoQuiz) root.pletoQuiz = new PurchaseFlowV3(root, config);
    window.purchaseFlowV3 = root.pletoQuiz;
  }

  window.PurchaseFlowV3Logic = { compile, buildSequence, recommend, klaviyoProperties };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-init when the section re-renders, show the selected block's screen.
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (e) => init(e.target));
    document.addEventListener('shopify:section:select', (e) => {
      if (e.target.querySelector?.('[data-pleto-quiz]')) init(e.target);
    });
    document.addEventListener('shopify:block:select', (e) => {
      const section = e.target.closest?.('.shopify-section');
      if (!section?.querySelector('[data-pleto-quiz]')) return;
      if (window.purchaseFlowV3?.root && !section.contains(window.purchaseFlowV3.root)) init(section);
      window.purchaseFlowV3?.preview(e.detail.blockId);
    });
  }
})();
